import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { recipesApi }     from '@/api/recipes';
import { ingredientsApi } from '@/api/ingredients';
import { suppliersApi }   from '@/api/suppliers';
import { PageHeader, Card, CardHeader, CardTitle, CardContent, Spinner } from '@/components/ui';
import { IceCream2, Package, Truck, BookOpen } from 'lucide-react';

export default function DashboardPage() {
  const { t } = useTranslation();

  const { data: recipes,     isLoading: lr } = useQuery({ queryKey: ['recipes', { page: 1, limit: 5 }],     queryFn: () => recipesApi.list({ page: 1, limit: 5 }) });
  const { data: ingredients, isLoading: li } = useQuery({ queryKey: ['ingredients', { page: 1, limit: 1 }], queryFn: () => ingredientsApi.list({ page: 1, limit: 1 }) });
  const { data: suppliers,   isLoading: ls } = useQuery({ queryKey: ['suppliers', { page: 1, limit: 1 }],   queryFn: () => suppliersApi.list({ page: 1, limit: 1 }) });

  const stats = [
    { label: t('dashboard.totalRecipes'),     value: recipes?.meta.total,     icon: <BookOpen size={20} className="text-blue-500" />,   href: '/recipes' },
    { label: t('dashboard.totalIngredients'), value: ingredients?.meta.total, icon: <Package size={20} className="text-green-500" />,  href: '/ingredients' },
    { label: t('dashboard.totalSuppliers'),   value: suppliers?.meta.total,   icon: <Truck size={20} className="text-orange-500" />,   href: '/suppliers' },
  ];

  return (
    <div>
      <PageHeader title={t('dashboard.title')}>
        <IceCream2 size={22} className="text-primary" />
      </PageHeader>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-8">
        {stats.map(stat => (
          <Link key={stat.href} to={stat.href} className="group">
            <Card className="hover:shadow-md transition-shadow">
              <CardContent className="flex items-center gap-4 p-5">
                <div className="rounded-lg bg-muted p-2.5">{stat.icon}</div>
                <div>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                  <p className="text-2xl font-bold">
                    {lr || li || ls ? '—' : (stat.value ?? 0)}
                  </p>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Recent recipes */}
      <Card>
        <CardHeader>
          <CardTitle>{t('dashboard.recentRecipes')}</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {lr ? (
            <Spinner className="py-8" />
          ) : recipes?.data.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">{t('common.noResults')}</p>
          ) : (
            <ul className="divide-y divide-border">
              {recipes?.data.map(recipe => (
                <li key={recipe.id} className="py-3">
                  <Link to={`/recipes/${recipe.id}`} className="flex items-center justify-between hover:text-primary transition-colors">
                    <div>
                      <p className="font-medium">{recipe.name}</p>
                      <p className="text-xs text-muted-foreground">{recipe.recipe_type_name ?? '—'}</p>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(recipe.updated_at).toLocaleDateString()}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
